import { useContext, useLayoutEffect } from 'react';

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5radar from "@amcharts/amcharts5/radar";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Dark from "@amcharts/amcharts5/themes/Dark";
import { DataContext } from 'contexts/DataContext';

interface GaugeProps {
  name: string;
  value: number;
  backgroundValue: number;
  height?: string;
}

const Gauge = (props: GaugeProps) => {
  const dataContext = useContext(DataContext);
  const isDark = !!dataContext.darkMode;

  useLayoutEffect(() => {
    // Create root and apply animated (light) theme
    const root = am5.Root.new(props.name);
    root._licenseApplied();
    root.setThemes(
      isDark
        ? [am5themes_Animated.new(root), am5themes_Dark.new(root)]
        : [am5themes_Animated.new(root)]
    );

    // Create chart
    const chart = root.container.children.push(
      am5radar.RadarChart.new(root, {
        panX: false,
        panY: false,
        startAngle: 180,
        endAngle: 360
      })
    );

    // Sfondo trasparente: il background resta quello della pagina/Paper
    chart.set("background", am5.Rectangle.new(root, {
      fillOpacity: 0
    }));

    // Format percentages
    chart.getNumberFormatter().set("numberFormat", "#'%'");

    // Create axis and renderer
    const axisRenderer = am5radar.AxisRendererCircular.new(root, {
      innerRadius: -40
    });

    axisRenderer.grid.template.setAll({
      stroke: am5.color(isDark ? 0x444444 : 0xDDDDDD),
      visible: true,
      strokeOpacity: 0.8
    });

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        maxDeviation: 0,
        min: 0,
        max: 100,
        strictMinMax: true,
        renderer: axisRenderer
      })
    );

    // Add clock hand
    const axisDataItem = xAxis.makeDataItem({});
    const clockHand = am5radar.ClockHand.new(root, {
      pinRadius: 50,
      radius: am5.percent(100),
      innerRadius: 50,
      bottomWidth: 0,
      topWidth: 0
    });

    // Stile lancetta come vecchio progetto: nessun pallone pieno, solo anello tratteggiato
    const strokeColor = isDark ? 0xffffff : 0x000000;
    clockHand.pin.setAll({
      fillOpacity: 0,
      strokeOpacity: 0.5,
      stroke: am5.color(strokeColor),
      strokeWidth: 1,
      strokeDasharray: [2, 2],
    });
    clockHand.hand.setAll({
      fillOpacity: 0,
      strokeOpacity: 0.5,
      stroke: am5.color(strokeColor),
      strokeWidth: 0.5,
    });

    const bullet = axisDataItem.set(
      "bullet",
      am5xy.AxisBullet.new(root, { sprite: clockHand })
    );

    xAxis.createAxisRange(axisDataItem);

    // Center label
    const label = chart.radarContainer.children.push(
      am5.Label.new(root, {
        centerX: am5.percent(50),
        textAlign: "center",
        centerY: am5.percent(50),
        fontSize: "1em",
        fill: am5.color(isDark ? 0xffffff : 0x000000)
      })
    );

    // Set value and update label su base props.value
    axisDataItem.set("value", props.value);
    label.set("text", Math.round(props.value).toString() + "%");
    bullet.get("sprite").on("rotation", () => {
      const current = axisDataItem.get("value");
      const value = (typeof current === 'number' ? current : props.value) as number;
      label.set("text", Math.round(value).toString() + "%");
    });

    chart.bulletsContainer.set("mask", undefined);

    // Background ranges
    const colorSet = am5.ColorSet.new(root, {});

    const axisRange0 = xAxis.createAxisRange(
      xAxis.makeDataItem({
        above: true,
        value: 0,
        endValue: props.backgroundValue
      })
    );
    axisRange0.get("axisFill")?.setAll({
      visible: true,
      fill: colorSet.getIndex(0)
    });
    axisRange0.get("label")?.setAll({ forceHidden: true });

    const axisRange1 = xAxis.createAxisRange(
      xAxis.makeDataItem({
        above: true,
        value: props.backgroundValue,
        endValue: 100
      })
    );
    axisRange1.get("axisFill")?.setAll({
      visible: true,
      fill: colorSet.getIndex(4)
    });
    axisRange1.get("label")?.setAll({ forceHidden: true });

    // Animate on load
    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark]);

  return <div id={props.name} style={{ width: "100%", height: props.height ?? "240px" }}></div>;
};

export default Gauge;